import { firstEmailSegment } from '../../../shared/utils/first-email-segment.util';
import { firstNameSegment } from '../../../shared/utils/first-name-segment.util';
import { normalizeListSearch } from '../../../shared/utils/normalize-list-search.util';
import { BaseService } from '../../base';
import type {
  CopyQuoteResponse,
  GetQuotesParams,
  Product,
  ProposalDetails,
  SendProposalEmailPayload,
  SummaryQuotes,
  SummaryQuotesApiItem,
  SummaryQuotesApiResponse,
  SummaryQuotesResponse,
} from '../types';

function mapApiItemToSummary(item: SummaryQuotesApiItem): SummaryQuotes {
  const statusValue = item.statusPortal ?? item.proposalStatus ?? 0;

  let proposalStatus: number;
  if (typeof statusValue === 'number') {
    proposalStatus = statusValue;
  } else if (typeof statusValue === 'string') {
    const parsed = parseInt(statusValue.trim(), 10);
    proposalStatus = isNaN(parsed) ? 0 : parsed;
  } else {
    proposalStatus = 0;
  }

  return {
    insuredName: firstNameSegment(item.insuredName) || '-',
    insuredEmail: firstEmailSegment(item.insuredEmail) || '-',
    registerNumber: firstNameSegment(item.registerNumber) || '',
    id: item.id || '',
    policyId: item.policyId,
    productName: item.productName || '-',
    productCode: item.productCode,
    proposalDate:
      item.proposalDate || item.quoteDate || new Date().toISOString(),
    proposalStatus,
    proposalNumber:
      item.proposalNumber ??
      (item.quoteNumber != null ? String(item.quoteNumber) : '-'),
    brokerName: item.brokerName || '-',
    inclusionUserName:
      item.inclusionUserName?.trim() || item.insertByUserName?.trim() || '-',
  };
}

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

const QUOTE_PORTAL_STATUSES = '1,17,3,2,4,5';

const DEFAULT_PRODUCT_TYPE_ID = import.meta.env.VITE_DEFAULT_PRODUCT_TYPE_ID as
  | string
  | undefined;

export class CommonQuotesService extends BaseService {
  constructor() {
    super();
  }

  async getProducts(productTypeId?: string): Promise<{
    success: boolean;
    data?: Product[];
    error?: string;
  }> {
    try {
      const id = productTypeId || DEFAULT_PRODUCT_TYPE_ID || '3';
      const path = `/portal/v1/products?productTypeId=${encodeURIComponent(id)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<Product[]>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao carregar produtos',
      };
    }
  }

  async copyQuote(
    policyId: string,
    productCode?: string
  ): Promise<{
    success: boolean;
    data?: CopyQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const encoded = encodeURIComponent(policyId);
      const path = `/portal/v1/quotes/${encoded}/copy`;
      const body = productCode ? { productCode } : {};
      const response = await this.post(path, body, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<CopyQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao clonar cotação',
      };
    }
  }

  async getQuotes(params: GetQuotesParams = {}): Promise<{
    success: boolean;
    data?: SummaryQuotesResponse;
    error?: string;
  }> {
    try {
      const {
        sortType = 'ASC',
        sortField = 'index_time',
        pageSize = 10,
        pageNumber = 1,
        search = '',
        status = '',
      } = params;
      const normalizedSearch = normalizeListSearch(search);

      const queryParams = new URLSearchParams({
        sortType,
        sortField,
        pageSize: pageSize.toString(),
        pageNumber: pageNumber.toString(),
        filter: normalizedSearch,
        status: status || QUOTE_PORTAL_STATUSES,
      });

      if (params.partyCode) queryParams.append('partyCode', params.partyCode);
      if (params.productCode)
        queryParams.append('productCode', params.productCode);
      if (params.proposalDateFrom)
        queryParams.append('proposalDateFrom', params.proposalDateFrom);
      if (params.proposalDateTo)
        queryParams.append('proposalDateTo', params.proposalDateTo);

      const path = `/portal/v1/quotes?${queryParams.toString()}`;

      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<SummaryQuotesApiResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar cotações',
        };
      }

      return {
        success: true,
        data: {
          data: (json.data.data || []).map(mapApiItemToSummary),
          meta: json.data.meta,
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao listar cotações',
      };
    }
  }

  async getProposalDetails(policyId: string): Promise<{
    success: boolean;
    data?: ProposalDetails;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/quotes/${encodeURIComponent(policyId)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<ProposalDetails>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar detalhes da proposta',
      };
    }
  }

  async sendProposalEmail(payload: SendProposalEmailPayload): Promise<{
    success: boolean;
    error?: string;
    message?: string;
  }> {
    try {
      const path = '/portal/v1/quotes/send-email';
      const normalizedPayload = {
        ...payload,
        stage: payload.stage || 'quote',
      };
      const response = await this.post(
        path,
        normalizedPayload,
        {},
        PORTAL_API_URL
      );
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao enviar e-mail',
      };
    }
  }
}

export const commonQuotesService = new CommonQuotesService();
